import { useEffect } from 'react';
import type { ParsedEmbedScript } from '../utils/parseEmbedHtml';

/**
 * Injects one or more third-party <script> tags via document.createElement +
 * appendChild rather than dangerouslySetInnerHTML — browsers don't execute
 * <script> tags inserted via innerHTML, so embeds (GHL, SWG, etc.) need this
 * real-DOM-node approach. Mirrors the pattern already used in
 * BlogPostView.tsx/NewsroomPostView.tsx for Subscribe-with-Google's script.
 *
 * `idPrefix` scopes the id-guard (e.g. a slot key) so the same slot rendered
 * twice on one page doesn't double-inject, while two different slots don't
 * collide with each other.
 */
export function useEmbedScripts(idPrefix: string, scripts: ParsedEmbedScript[]) {
  const key = scripts.map((s) => `${s.src || ''}::${s.innerHTML || ''}`).join('||');

  useEffect(() => {
    if (scripts.length === 0) return;
    const ids: string[] = [];

    scripts.forEach((spec, i) => {
      const id = `${idPrefix}-script-${i}`;
      ids.push(id);
      if (document.getElementById(id)) return;

      const script = document.createElement('script');
      script.id = id;
      script.async = true;
      if (spec.src) script.src = spec.src;
      if (spec.innerHTML) script.textContent = spec.innerHTML;
      document.body.appendChild(script);
    });

    return () => {
      ids.forEach((id) => document.getElementById(id)?.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` already captures scripts' identity
  }, [idPrefix, key]);
}
